import type { AdminCardConfig, ModuleCardCenter, ModuleVO } from '@/api/system/module'
import { AI_APP_CENTER_HIDDEN_MODULE_KEYS, AI_APP_CENTER_MODULES } from '@/constants/aiAppCenter'
import { pathResolve } from '@/utils/routerHelper'
import { normalizeStandaloneTargetPath, resolveStandaloneAppKey } from '@/utils/standaloneApp'

export interface ModuleCard {
  key: string
  label: string
  desc: string
  path: string
  standalonePath?: string
  openMode: 'same-window' | 'new-window'
  icon: string
  /** 后台上传的卡片图标图；有值时优先于 icon 渲染 */
  iconUrl?: string
  color: string
}

export interface ModuleCardDisplay {
  label: string
  icon: string
  iconUrl?: string
  color: string
}

/** 前端模块注册表兜底：线上 system_module.config 漏写 adminCard 时仍可进 AI 应用 */
const registryAdminCard = (moduleName: string): AdminCardConfig | undefined => {
  const key = resolveStandaloneAppKey(moduleName)
  const reg = AI_APP_CENTER_MODULES.find((item) => item.key === key || item.key === moduleName)
  if (!reg) return undefined
  return {
    visible: true,
    centers: ['ai-app'],
    entryPath: reg.path,
    homePath: reg.homePath || reg.path,
    standalonePath: reg.standalonePath,
    openMode: 'new-window',
    icon: reg.icon,
    color: reg.color
  }
}

const parseAdminCard = (config?: string, moduleName?: string): AdminCardConfig | undefined => {
  let card: AdminCardConfig | undefined
  if (config) {
    try {
      const parsed = JSON.parse(config)
      if (parsed?.adminCard && typeof parsed.adminCard === 'object') {
        card = parsed.adminCard as AdminCardConfig
      }
    } catch {
      /* ignore */
    }
  }
  const fallback = moduleName ? registryAdminCard(moduleName) : undefined
  if (!card) return fallback
  if (!fallback) return card
  // 库表有残缺 adminCard 时，用注册表补齐关键路径字段
  return {
    ...fallback,
    ...card,
    visible: card.visible ?? fallback.visible,
    centers: card.centers?.length ? card.centers : fallback.centers,
    entryPath: card.entryPath || fallback.entryPath,
    homePath: card.homePath || fallback.homePath,
    standalonePath: card.standalonePath || fallback.standalonePath || fallback.homePath,
    // 注册表声明 new-window 的 AI 应用，不被库表误配的 same-window 覆盖
    openMode:
      fallback.openMode === 'new-window'
        ? 'new-window'
        : card.openMode || fallback.openMode || 'new-window',
    icon: card.icon || fallback.icon,
    iconUrl: card.iconUrl || fallback.iconUrl,
    color: card.color || fallback.color
  }
}

/** 模块管理「核心业务模块」推荐开关（配置键仍为 homeShortcut） */
export const isHomeCoreModuleEnabled = (module: ModuleVO): boolean => {
  return parseAdminCard(module.config, module.name)?.homeShortcut === true
}

/** @deprecated 语义已改为核心业务模块，请用 isHomeCoreModuleEnabled */
export const isHomeShortcutEnabled = isHomeCoreModuleEnabled

/** 工作台核心业务模块最多推荐数 */
export const HOME_CORE_MODULE_LIMIT = 12

/** 按标题/模块名兜底图标：库表 icon 常为空、旧 fa:、或无效名导致首页空白方块 */
const MODULE_ICON_BY_TITLE: Record<string, string> = {
  商家管理: 'ep:shop',
  公众号管理: 'ep:chat-dot-round',
  支付管理: 'ep:wallet',
  即时通讯: 'ep:chat-line-round',
  IM即时通讯: 'ep:chat-line-round',
  IM: 'ep:chat-line-round',
  小程序管理: 'ep:cellphone',
  会员管理: 'ep:user',
  会员中心: 'ep:user',
  系统管理: 'ep:setting',
  基础设施: 'ep:monitor',
  工作流程: 'ep:connection',
  模板管理: 'ep:brush',
  域名管理: 'ep:link',
  站点管理: 'ep:house'
}

const MODULE_ICON_BY_NAME: Record<string, string> = {
  merchant: 'ep:shop',
  mp: 'ep:chat-dot-round',
  pay: 'ep:wallet',
  im: 'ep:chat-line-round',
  member: 'ep:user',
  system: 'ep:setting',
  infra: 'ep:monitor',
  bpm: 'ep:connection'
}

/** 是否为可展示的图标图片地址（与 Iconify 名分流） */
export const isModuleIconImageUrl = (value?: string) => {
  const v = String(value || '').trim()
  if (!v) return false
  if (/^(https?:)?\/\//i.test(v)) return true
  if (v.startsWith('data:image')) return true
  // 站内上传路径，排除纯路由误判：须像资源文件
  if (v.startsWith('/') && /\.(png|jpe?g|gif|webp|svg|ico)(\?.*)?$/i.test(v)) return true
  return false
}

const isUsableIconName = (icon?: string) => {
  const value = String(icon || '').trim()
  if (!value) return false
  // 图片 URL / 本地路径不当作 Iconify 名
  if (isModuleIconImageUrl(value) || value.startsWith('data:')) return false
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('/')) return false
  // 旧 FontAwesome 前缀未接入集合时会空白
  if (/^(fa[sbrl]?:|fa-)/i.test(value)) return false
  return true
}

const resolveModuleIcon = (module: ModuleVO, cardIcon?: string) => {
  if (isUsableIconName(cardIcon)) return String(cardIcon).trim()
  if (isUsableIconName(module.icon)) return String(module.icon).trim()
  const byTitle = MODULE_ICON_BY_TITLE[module.title]
  if (byTitle) return byTitle
  const key = resolveStandaloneAppKey(module.name) || module.name
  return MODULE_ICON_BY_NAME[key] || MODULE_ICON_BY_NAME[module.name] || 'ep:grid'
}

const resolveModuleIconUrl = (module: ModuleVO, cardIconUrl?: string) => {
  const fromCard = String(cardIconUrl || '').trim()
  if (isModuleIconImageUrl(fromCard)) return fromCard
  const fromModule = String(module.icon || '').trim()
  if (isModuleIconImageUrl(fromModule)) return fromModule
  return undefined
}

export const getModuleCardDisplay = (module: ModuleVO): ModuleCardDisplay => {
  const card = parseAdminCard(module.config, module.name)
  return {
    label: module.title,
    icon: resolveModuleIcon(module, card?.icon),
    iconUrl: resolveModuleIconUrl(module, card?.iconUrl),
    color: card?.color || '#1677ff'
  }
}

const CENTER_PATH_PREFIXES = ['/ai-business-center', '/ai-Application-center'] as const

const routeExists = (routes: AppRouteRecordRaw[], target: string, parent = '/'): boolean => {
  const normalizedTarget = pathResolve('/', target)
  return routes.some((route) => {
    const fullPath = pathResolve(parent, route.path)
    return (
      fullPath === normalizedTarget || routeExists(route.children || [], normalizedTarget, fullPath)
    )
  })
}

/** homePath 常写 /mes/dashboard，实际路由可能挂在 /ai-business-center/mes/dashboard */
const cardPathExists = (routes: AppRouteRecordRaw[], target?: string): boolean => {
  if (!target) return false
  if (routeExists(routes, target)) return true
  const normalized = pathResolve('/', target)
  if (CENTER_PATH_PREFIXES.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`))) {
    return false
  }
  return CENTER_PATH_PREFIXES.some((prefix) => routeExists(routes, `${prefix}${normalized}`))
}

/** DB 常写短路径 /ai-education/overview，卡片入口需落到中心前缀完整路径，避免独立窗 about:blank */
const resolveCardNavPath = (
  routes: AppRouteRecordRaw[],
  candidates: Array<string | undefined>
): string | undefined => {
  for (const raw of candidates) {
    if (!raw || raw === 'about:blank' || raw === '/') continue
    const normalized = pathResolve('/', raw)
    if (cardPathExists(routes, normalized)) {
      if (routeExists(routes, normalized)) return normalized
      for (const prefix of CENTER_PATH_PREFIXES) {
        const prefixed = `${prefix}${normalized}`
        if (routeExists(routes, prefixed)) return prefixed
      }
      return normalized
    }
  }
  return candidates.find((p) => !!p && p !== 'about:blank' && p !== '/')
}

const findFirstModulePath = (
  routes: AppRouteRecordRaw[],
  moduleCode: string,
  parent = '/'
): string | undefined => {
  for (const route of routes) {
    const fullPath = pathResolve(parent, route.path)
    // 优先返回 root 的 redirect（概览页），再深入子路由
    if (route.meta?.moduleCode === moduleCode && !route.meta?.hidden) {
      const redirect =
        typeof route.redirect === 'string' && route.redirect.startsWith('/')
          ? route.redirect
          : undefined
      return redirect || fullPath
    }
    const childPath = findFirstModulePath(route.children || [], moduleCode, fullPath)
    if (childPath) return childPath
  }
  return undefined
}

const collectModuleCodes = (routes: AppRouteRecordRaw[], result = new Set<string>()) => {
  routes.forEach((route) => {
    if (typeof route.meta?.moduleCode === 'string') {
      result.add(route.meta.moduleCode)
    }
    collectModuleCodes(route.children || [], result)
  })
  return result
}

export const buildModuleCards = (
  modules: ModuleVO[],
  center: ModuleCardCenter,
  menuRoutes: AppRouteRecordRaw[],
  allRoutes: AppRouteRecordRaw[],
  hasFullAccess = false
): ModuleCard[] => {
  const authorizedModuleCodes = collectModuleCodes(menuRoutes)
  return modules.flatMap((module): ModuleCard[] => {
    const resolvedKey = resolveStandaloneAppKey(module.name)
    if (
      center === 'ai-app' &&
      ((AI_APP_CENTER_HIDDEN_MODULE_KEYS as readonly string[]).includes(module.name) ||
        (AI_APP_CENTER_HIDDEN_MODULE_KEYS as readonly string[]).includes(resolvedKey))
    ) {
      return []
    }
    const card = parseAdminCard(module.config, module.name)
    if (
      !card?.visible ||
      !card.centers?.includes(center) ||
      (!hasFullAccess && !authorizedModuleCodes.has(module.name))
    ) {
      return []
    }
    const fallbackPath = findFirstModulePath(menuRoutes, module.name)
    const path = resolveCardNavPath(allRoutes, [
      card.homePath,
      card.entryPath,
      card.standalonePath,
      fallbackPath
    ])
    if (!path) return []
    const display = getModuleCardDisplay(module)
    // 注册表完整 standalone 优先；库表缺省时用已解析的中心前缀 path
    const registryStandalone = registryAdminCard(module.name)?.standalonePath
    const rawStandalone = card.standalonePath || registryStandalone || path
    return [
      {
        // 独立窗 app= 用语义化 key（ai→aiModel，o2o→visiting，capacity→distribution）；鉴权仍按 module.name
        key: resolvedKey,
        label: display.label,
        desc: module.description || '',
        path: normalizeStandaloneTargetPath(path),
        standalonePath: rawStandalone
          // plain 模式直接新窗口打开完整管理路由（不做独立壳前缀归一，避免 /ai-Application-center/catering/dashboard 被剥成 /catering/dashboard）
          ? card.openMode === 'plain'
            ? rawStandalone
            : normalizeStandaloneTargetPath(rawStandalone)
          : undefined,
        // AI 应用中心卡片本质是「打开独立应用」，默认新窗口打开；
        // 仅当后端明确 same-window（如内嵌到当前窗的业务模块）时才同窗。
        // 缺失 openMode 时不再落到 same-window（避免 AI智慧餐饮等打不开新窗口）。
        openMode: card.openMode === 'same-window' ? 'same-window' : 'new-window',
        icon: display.icon,
        iconUrl: display.iconUrl,
        color: display.color
      }
    ]
  })
}
